import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Upload as UpIcon, FileDown } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Stat, Table, download } from "./shared";

function Preview({ p, onDone }) {
  const [policy, setPolicy] = useState("skip"), [busy, setBusy] = useState(false), qc = useQueryClient();
  const go = () => {
    setBusy(true);
    api.post(`/outbound/imports/${p.id}/confirm`, { confirm: true, duplicate_policy: policy })
      .then((r) => { toast.success(`Imported ${r.data.added}, updated ${r.data.updated}, skipped ${r.data.skipped}`); qc.invalidateQueries({ queryKey: ["ob"] }); onDone(); })
      .catch((e) => toast.error(apiErr(e))).finally(() => setBusy(false));
  };
  const rows = p.rows.map((r) => [r.row, r.data.customer_name, r.data.phone_number,
    r.eligible ? <span className="font-semibold text-emerald-700">Eligible</span> : <span className="text-rose-700">{[...r.errors, ...r.exclusions, r.duplicate_in_file].filter(Boolean).join("; ")}</span>,
    r.existing_contact_id ? "Existing contact" : r.crm_customer_id ? "Matches CRM customer" : ""]);
  return (
    <div className="space-y-3" data-testid="ob-import-preview">
      <p className="rounded-lg bg-amber-50 p-2 text-xs text-amber-900">{p.note}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        <Stat k="Rows" v={p.total} id="ob-prev-total" /><Stat k="Eligible" v={p.eligible} id="ob-prev-eligible" />
        <Stat k="Invalid (fix the file)" v={p.invalid} id="ob-prev-invalid" /><Stat k="Excluded" v={p.excluded} id="ob-prev-excluded" />
        <Stat k="Duplicates in file" v={p.duplicates_in_file} id="ob-prev-dups" />
      </div>
      {Object.keys(p.reasons).length > 0 && <ul className="list-disc pl-5 text-xs text-slate-600" data-testid="ob-prev-reasons">{Object.entries(p.reasons).map(([k, v]) => <li key={k}>{v} × {k}</li>)}</ul>}
      <Table head={["Row", "Name", "Phone", "Result", "Match"]} rows={rows} id="ob-prev-table" empty="No rows" />
      {p.existing_contacts > 0 && (
        <label className="flex items-center gap-2 text-xs">Contacts already in your list ({p.existing_contacts}):
          <select value={policy} onChange={(e) => setPolicy(e.target.value)} className="rounded border px-2 py-1" data-testid="ob-dup-policy">
            <option value="skip">Keep existing, skip these rows</option><option value="update">Update existing with these rows</option>
          </select></label>)}
      <Button onClick={go} disabled={busy || !p.eligible} data-testid="ob-import-confirm">Import {p.eligible} eligible contacts</Button>
    </div>
  );
}

function Mapping({ m, map, setMap }) {
  return (
    <div className="space-y-1 rounded-xl border border-slate-200 bg-white p-3 text-xs" data-testid="ob-map">
      <p className="font-semibold">Match your columns ({m.rows} rows). Required: {m.required.join(", ")}</p>
      <div className="grid gap-1 sm:grid-cols-2">{m.headers.map((h) => (
        <label key={h} className="flex items-center justify-between gap-2"><span className="truncate">{h}</span>
          <select value={map[h] || ""} onChange={(e) => setMap({ ...map, [h]: e.target.value })} className="rounded border px-1 py-0.5" data-testid={`ob-map-${h.replace(/\W+/g, "_")}`}>
            <option value="">Ignore</option>{m.columns.map((c) => <option key={c} value={c}>{c}</option>)}</select></label>))}</div>
    </div>
  );
}

export function UploadContacts({ types }) {
  const [file, setFile] = useState(null), [type, setType] = useState("grooming_reminder"), [p, setP] = useState(null), [busy, setBusy] = useState(false);
  const [m, setM] = useState(null), [map, setMap] = useState({});
  const pick = (f) => {
    setFile(f); setP(null); setM(null);
    if (!f) return;
    const d = new FormData();
    d.append("file", f);
    api.post("/outbound/imports/headers", d).then((r) => { setM(r.data); setMap(r.data.suggested); }).catch((e) => toast.error(apiErr(e)));
  };
  const send = () => {
    const f = new FormData();
    f.append("file", file);
    f.append("campaign_type", type);
    f.append("mapping", JSON.stringify(map));
    setBusy(true);
    api.post("/outbound/imports", f).then((r) => setP(r.data)).catch((e) => toast.error(apiErr(e))).finally(() => setBusy(false));
  };
  return (
    <div className="space-y-4" data-testid="ob-upload">
      <div className="flex flex-wrap gap-2">
        <Button size="sm" variant="outline" onClick={() => download("csv")} data-testid="ob-template-csv"><FileDown className="mr-1 h-4 w-4" />CSV template</Button>
        <Button size="sm" variant="outline" onClick={() => download("xlsx")} data-testid="ob-template-xlsx"><FileDown className="mr-1 h-4 w-4" />Excel template</Button>
      </div>
      <p className="text-xs text-slate-600">Required for every campaign: customer_name, phone_number, consent_status, consent_source, consent_date. A phone number alone is never treated as permission to call.</p>
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white p-4">
        <select value={type} onChange={(e) => setType(e.target.value)} className="rounded border px-2 py-1.5 text-sm" data-testid="ob-upload-type">
          {Object.entries(types).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <input type="file" accept=".csv,.xlsx" onChange={(e) => pick(e.target.files[0])} className="text-sm" data-testid="ob-upload-file" />
        <Button size="sm" onClick={send} disabled={!file || busy} data-testid="ob-upload-check"><UpIcon className="mr-1 h-4 w-4" />{busy ? "Checking…" : "Check file"}</Button>
      </div>
      {type && <p className="text-xs text-slate-500" data-testid="ob-upload-required">This campaign type needs: {types[type]?.required.join(", ")}</p>}
      {m && !p && <Mapping m={m} map={map} setMap={setMap} />}
      {p && <Preview p={p} onDone={() => { setP(null); setFile(null); setM(null); }} />}
    </div>
  );
}
